import React, { useState } from "react";

function EditHistory({
    language,
    loading,
    editLogs,
    pagination = { page: 1, per_page: 20, total: 0, pages: 1 },
    onPageChange,
}) {
    const [filterAction, setFilterAction] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [expandedLogId, setExpandedLogId] = useState(null);

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return "N/A";

        if (timestamp._seconds) {
            return new Date(timestamp._seconds * 1000).toLocaleString(
                language === "zh" ? "zh-TW" : "en-US",
                {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                },
            );
        }

        return new Date(timestamp).toLocaleString(
            language === "zh" ? "zh-TW" : "en-US",
            {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            },
        );
    };

    // Filter logs based on selections
    const filteredLogs = editLogs.filter((log) => {
        if (filterAction && log.update_type !== filterAction) return false;
        if (filterStatus && log.status !== filterStatus) return false;
        return true;
    });

    // Action Badge Component
    const ActionBadge = ({ type }) => {
        const config = {
            create: {
                bg: "bg-green-500/10",
                text: "text-green-600",
                border: "border-green-500/20",
                label: language === "zh" ? "新增" : "CREATE",
            },
            update: {
                bg: "bg-blue-500/10",
                text: "text-blue-600",
                border: "border-blue-500/20",
                label: language === "zh" ? "更新" : "UPDATE",
            },
            delete: {
                bg: "bg-red-500/10",
                text: "text-red-600",
                border: "border-red-500/20",
                label: language === "zh" ? "刪除" : "DELETE",
            },
        };

        const c = config[type] || config.update;

        return (
            <span
                className={`rounded-lg inline-block px-2.5 py-1 text-xs font-medium uppercase border ${c.bg} ${c.text} ${c.border}`}
            >
                {c.label}
            </span>
        );
    };

    // Status Badge Component
    const StatusBadge = ({ status }) => {
        const config = {
            success: {
                bg: "bg-green-500/10",
                text: "text-green-600",
                border: "border-green-500/20",
                label: language === "zh" ? "成功" : "Success",
            },
            failed: {
                bg: "bg-red-500/10",
                text: "text-red-600",
                border: "border-red-500/20",
                label: language === "zh" ? "失敗" : "Failed",
            },
            pending: {
                bg: "bg-yellow-500/10",
                text: "text-yellow-600",
                border: "border-yellow-500/20",
                label: language === "zh" ? "處理中" : "Pending",
            },
        };

        const c = config[status] || config.pending;

        return (
            <span
                className={`rounded-lg inline-block px-2.5 py-1 text-xs font-medium border ${c.bg} ${c.text} ${c.border}`}
            >
                {c.label}
            </span>
        );
    };

    const renderChanges = (changes) => {
        if (!changes || Object.keys(changes).length === 0) {
            return (
                <div className="text-gray-600 text-sm p-4 bg-gray-50 rounded-lg border border-gray-200">
                    {language === "zh" ? "無更改記錄" : "No changes recorded"}
                </div>
            );
        }

        return (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                {language === "zh" ? "欄位" : "Field"}
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                {language === "zh" ? "舊值" : "Previous Value"}
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                {language === "zh" ? "新值" : "New Value"}
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {Object.entries(changes).map(([field, change]) => (
                            <tr
                                key={field}
                                className="hover:bg-gray-50 transition-colors"
                            >
                                <td className="px-4 py-3 text-sm font-medium text-gray-900 capitalize">
                                    {field.replace(/_/g, " ")}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600 font-mono">
                                    {change.old_value !== null &&
                                    change.old_value !== undefined
                                        ? typeof change.old_value === "object"
                                            ? JSON.stringify(change.old_value)
                                            : String(change.old_value)
                                        : language === "zh"
                                          ? "—"
                                          : "—"}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900 font-mono font-medium">
                                    {change.new_value !== null &&
                                    change.new_value !== undefined
                                        ? typeof change.new_value === "object"
                                            ? JSON.stringify(change.new_value)
                                            : String(change.new_value)
                                        : language === "zh"
                                          ? "—"
                                          : "—"}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    // Timeline Log Item Component
    const LogItem = ({ log, index }) => {
        const isExpanded = expandedLogId === log.id;

        return (
            <div
                className={`relative pl-8 pb-6 ${
                    index !== filteredLogs.length - 1
                        ? "border-l-2 border-gray-200"
                        : ""
                }`}
            >
                {/* Timeline dot */}
                <div
                    className={`absolute -left-2.5 w-5 h-5 rounded-full border-2 border-white shadow-md ${
                        log.update_type === "create"
                            ? "bg-green-500"
                            : log.update_type === "delete"
                              ? "bg-red-500"
                              : "bg-blue-500"
                    }`}
                />

                {/* Log Card */}
                <div
                    className={`group relative bg-white rounded-2xl border transition-all duration-300 ${
                        isExpanded
                            ? "border-orange-500/50 shadow-xl shadow-orange-500/10"
                            : "border-gray-200 hover:border-orange-500/30 hover:shadow-lg"
                    }`}
                >
                    {/* Hover gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl pointer-events-none" />

                    {/* Main content row */}
                    <div
                        className="relative p-5 cursor-pointer"
                        onClick={() =>
                            setExpandedLogId(isExpanded ? null : log.id)
                        }
                    >
                        <div className="flex items-center justify-between gap-4">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 mb-3">
                                    <ActionBadge type={log.update_type} />
                                    <span className="font-bold text-gray-900 uppercase">
                                        {log.product_id}
                                        {log.product_name
                                            ? ` - ${log.product_name}`
                                            : ""}
                                    </span>
                                    <StatusBadge status={log.status} />
                                    {log.product_deleted && (
                                        <span className="rounded-lg inline-block px-2.5 py-1 text-xs font-medium border bg-gray-500/10 text-gray-600 border-gray-500/20">
                                            {language === "zh"
                                                ? "產品已刪除"
                                                : "Product deleted"}
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center gap-4 text-sm text-gray-600">
                                    <span className="flex items-center gap-1.5">
                                        <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                            />
                                        </svg>
                                        {formatTimestamp(log.timestamp)}
                                    </span>
                                    <span className="text-gray-400">•</span>
                                    <span className="flex items-center gap-1.5">
                                        <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                            />
                                        </svg>
                                        {log.user_id || "System"}
                                    </span>
                                </div>
                            </div>

                            <button className="text-gray-400 hover:text-orange-500 transition-colors p-2">
                                <svg
                                    className={`w-5 h-5 transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 9l-7 7-7-7"
                                    />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Expandable content */}
                    {isExpanded && (
                        <div className="relative rounded-b-2xl border-t border-gray-200 bg-gray-50 p-5">
                            {/* Error message if failed */}
                            {log.status === "failed" && log.error && (
                                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                                    <div className="flex items-start gap-2">
                                        <svg
                                            className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                        <div className="flex-1">
                                            <p className="text-sm font-semibold text-red-800 mb-1">
                                                {language === "zh"
                                                    ? "錯誤訊息"
                                                    : "Error Message"}
                                            </p>
                                            <p className="text-sm text-red-700 font-mono">
                                                {log.error}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Changes header */}
                            <h4 className="font-semibold text-gray-900 mb-3 text-sm uppercase flex items-center gap-2">
                                <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                    />
                                </svg>
                                {language === "zh"
                                    ? "變更詳情"
                                    : "Change Details"}
                            </h4>

                            {renderChanges(log.changes)}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-6">
            {/* Header Section */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900 uppercase mb-1">
                            {language === "zh"
                                ? "編輯歷史記錄"
                                : "Edit History"}
                        </h2>
                        <p className="text-gray-600 text-sm">
                            {language === "zh"
                                ? `本頁 ${filteredLogs.length} 筆，共 ${pagination.total} 筆記錄`
                                : `${filteredLogs.length} on this page of ${pagination.total} entries`}
                        </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                        {/* Action Filter */}
                        <select
                            value={filterAction}
                            onChange={(e) => setFilterAction(e.target.value)}
                            className="px-4 py-2.5 rounded-xl bg-white border border-gray-300 text-gray-700 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 text-sm transition-all"
                        >
                            <option value="">
                                {language === "zh" ? "所有操作" : "All Actions"}
                            </option>
                            <option value="create">
                                {language === "zh" ? "創建" : "Created"}
                            </option>
                            <option value="update">
                                {language === "zh" ? "更新" : "Updated"}
                            </option>
                            <option value="delete">
                                {language === "zh" ? "刪除" : "Deleted"}
                            </option>
                        </select>

                        {/* Status Filter */}
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="px-4 py-2.5 rounded-xl bg-white border border-gray-300 text-gray-700 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 text-sm transition-all"
                        >
                            <option value="">
                                {language === "zh" ? "所有狀態" : "All Status"}
                            </option>
                            <option value="success">
                                {language === "zh" ? "成功" : "Success"}
                            </option>
                            <option value="failed">
                                {language === "zh" ? "失敗" : "Failed"}
                            </option>
                            <option value="pending">
                                {language === "zh" ? "處理中" : "Pending"}
                            </option>
                        </select>

                        {/* Clear filters button */}
                        {(filterAction || filterStatus) && (
                            <button
                                onClick={() => {
                                    setFilterAction("");
                                    setFilterStatus("");
                                }}
                                className="px-4 py-2.5 text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300 rounded-xl transition-all"
                            >
                                {language === "zh" ? "清除" : "Clear Filters"}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Timeline Content */}
            {loading ? (
                <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-gray-200">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
                    <p className="text-gray-600 mt-4 font-medium">
                        {language === "zh"
                            ? "加載歷史記錄中..."
                            : "Loading history..."}
                    </p>
                </div>
            ) : filteredLogs.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-gray-200">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                        <svg
                            className="w-8 h-8 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                        </svg>
                    </div>
                    <p className="text-gray-900 font-semibold mb-2 text-lg">
                        {language === "zh" ? "暫無日誌記錄" : "No logs found"}
                    </p>
                    <p className="text-gray-600 text-sm">
                        {language === "zh"
                            ? "產品更改將顯示在此處"
                            : "Product changes will appear here"}
                    </p>
                </div>
            ) : (
                <div className="bg-white rounded-2xl border border-gray-200 p-6">
                    {filteredLogs.map((log, index) => (
                        <LogItem key={log.id} log={log} index={index} />
                    ))}
                </div>
            )}

            {/* Pagination */}
            {pagination.pages > 1 && !loading && (
                <div className="flex items-center justify-between bg-white rounded-xl px-6 py-4 border border-gray-200 shadow-sm">
                    <p className="text-sm text-gray-500">
                        {language === "zh"
                            ? `第 ${pagination.page} 頁，共 ${pagination.pages} 頁 (${pagination.total} 筆記錄)`
                            : `Page ${pagination.page} of ${pagination.pages} (${pagination.total} entries)`}
                    </p>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => onPageChange?.(pagination.page - 1)}
                            disabled={pagination.page <= 1 || loading}
                            className="p-2 rounded-lg text-gray-500 hover:bg-orange-50 hover:text-orange-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                        >
                            <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 19l-7-7 7-7"
                                />
                            </svg>
                        </button>

                        {Array.from(
                            { length: Math.min(pagination.pages, 7) },
                            (_, i) => {
                                // Show pages around the current page
                                let p;
                                if (pagination.pages <= 7) {
                                    p = i + 1;
                                } else if (pagination.page <= 4) {
                                    p = i + 1;
                                } else if (pagination.page >= pagination.pages - 3) {
                                    p = pagination.pages - 6 + i;
                                } else {
                                    p = pagination.page - 3 + i;
                                }
                                return (
                                    <button
                                        key={p}
                                        onClick={() => onPageChange?.(p)}
                                        disabled={loading}
                                        className={`w-8 h-8 rounded-lg text-sm font-medium transition-all ${
                                            p === pagination.page
                                                ? "bg-orange-500 text-white shadow-sm"
                                                : "text-gray-600 hover:bg-orange-50 hover:text-orange-500"
                                        }`}
                                    >
                                        {p}
                                    </button>
                                );
                            },
                        )}

                        <button
                            onClick={() => onPageChange?.(pagination.page + 1)}
                            disabled={
                                pagination.page >= pagination.pages || loading
                            }
                            className="p-2 rounded-lg text-gray-500 hover:bg-orange-50 hover:text-orange-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                        >
                            <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5l7 7-7 7"
                                />
                            </svg>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default EditHistory;
